import { z } from 'zod';
import type {
  activitiesActivityFlowProjectPathObj,
  fundBusinessIdProjectPathObj,
  legalBasesProjectPathObj,
  paymentAmountBreakdownPathObj,
  paymentGroupProjectPathObj,
  planProjectPathObj,
  projectPathObj,
  rootActivityPathObj,
} from '../models/pinpoint-path';

export const KsStandardBasicInformationScenePinpointFieldEnum = z.enum([
  'ks.standard.organization-in-charge',
  'ks.standard.people-in-charge',
  'ks.standard.business-manager',
  'ks.standard.other-organization-in-charge',
  'ks.standard.related-organizations',
  'ks.standard.additional-organization-in-charge',
  'ks.standard.project-number',
  'ks.standard.project-group',
  'ks.standard.fund-sheet-type',
  'ks.standard.fund-sheet-number',
  'ks.standard.fund-sheet-branch-number',
  'ks.standard.fundOwner',
  'ks.standard.corporate-form',
  'ks.standard.optional-corporate-form',
  'ks.standard.eligibility-inspection-method-and-reasons-for-selection',
  'ks.standard.eligibility-inspection-results',
  'ks.standard.implementation-status-of-inspection',
  'ks.standard.operation-form',
  'ks.standard.business-form',
  'ks.standard.related-projects',
  'ks.standard.project-purpose',
  'ks.standard.project-task',
  'ks.standard.overview',
  'ks.standard.overview-url',
  'ks.standard.fund-necessity-type',
  'ks.standard.fund-necessity-reasons-for-local-government',
  'ks.standard.start-year',
  'ks.standard.end-year',
  'ks.standard.end-year-supplementary-reasons',
  'ks.standard.reason-for-changing-scheduled-end-period',
  'ks.standard.end-date-of-new-application',
  'ks.standard.end-date-of-new-application-supplementary-reasons',
  'ks.standard.reason-for-changing-end-date-of-new-application',
  'ks.standard.legal-bases-title',
  'ks.standard.legal-bases',
  'ks.standard.plan-title',
  'ks.standard.plan',
  'ks.standard.remark',
  'ks.standard.project-reference',
  'ks.standard.legal-basis',
  'ks.standard.beneficiary-info',
  'ks.standard.related-project-for-other-ministry',
  'ks.standard.old-project-number',
]);

export const KsStandardFundAdditionalAttributesScenePinpointFieldEnum = z.enum([
  'ks.standard.composition-backgrounds-title',
  'ks.standard.fund-budget-classification',
  'ks.standard.recent-budget-measure-year',
  'ks.standard.budget-reason-with-achievement-status-and-review-result',
  'ks.standard.next-budget-measure-review-year',
  'ks.standard.national-treasury-return-background-title',
  'ks.standard.effort-and-result-overview',
  'ks.standard.matter-of-act-for-normalization-of-grant',
]);

export const KsStandardActivitiesScenePinpointFieldEnum = z.enum([
  'ks.standard.cabinet-decision-with-defined-indicators-name',
  'ks.standard.cabinet-decision-with-defined-indicators-url',
  'ks.standard.cabinet-decision-with-defined-indicators-relevant-part',
  'ks.standard.activity-title',
  'ks.standard.activity',
  'ks.standard.activities-and-outcomes-title',
]);

export const KsStandardActivityFlowScenePinpointFieldEnum = z.enum([
  'ks.standard.indicator-title',
  'ks.standard.indicator-description',
  'ks.standard.activity-and-result-target',
  'ks.standard.achievement-for-qualitative-outcome',
  'ks.standard.indicator-setting-reason',
  'ks.standard.reason-for-setting-qualitative-outcome',
  'ks.standard.reason-why-outcomes-cannot-be-multiple',
  'ks.standard.connection-to-next-outcome',
]);

export const KsStandardIncomeAndExpenditureScenePinpointFieldEnum = z.enum([
  'ks.standard.income-and-expenditure-table',
  'ks.standard.situation-of-divergence-in-execution-two-years-ago',
  'ks.standard.situation-of-divergence-in-execution-one-years-ago',
]);

export const KsStandardFundBusinessScenePinpointFieldEnum = z.enum([
  'ks.standard.fund-business-table',
]);

export const KsStandardOwnershipRatioScenePinpointFieldEnum = z.enum([
  'ks.standard.ownership-ratio',
  'ks.standard.ownership-ratio-numerator',
  'ks.standard.ownership-ratio-denominator',
  'ks.standard.contents-of-held-fund-amount-of-ownership-ratio',
  'ks.standard.contents-of-fund-project-costs-of-ownership-ratio',
  'ks.standard.concept-of-contents-of-each-term',
  'ks.standard.basis-for-estimating-indicators-for-project-prospect',
  'ks.standard.indicators-for-project-prospect-and-recent-performance',
  'ks.standard.has-ceased-operations',
  'ks.standard.has-no-operation-fund-detected',
  'ks.standard.has-fund-that-lost-purpose',
  'ks.standard.has-exceed-1-significantly',
  'ks.standard.has-deemed-unlikely-to-be-used',
  'ks.standard.reasons-for-selecting-absense-when-ownership-ratio-exceeds-1',
  'ks.standard.consideration-where-the-fund-are-unlikely-to-be-used',
  'ks.standard.reason-for-leaving-the-fund-that-are-unlikely-to-be-used',
]);

export const KsStandardInspectionEvaluationScenePinpointFieldEnum = z.enum([
  'ks.standard.inspection-result',
  'ks.standard.evaluation',
  'ks.standard.direction-of-improvement',
  'ks.standard.findings-of-external-experts',
  'ks.standard.process-leading-to-the-findings-of-review-promotion-team',
  'ks.standard.improvement-point-based-on-the-findings',
  'ks.standard.analysis-of-validity',
]);

export const KsStandardPaymentScenePinpointFieldEnum = z.enum([
  'ks.standard.payment-title',
  'ks.standard.payment-group-row',
  'ks.standard.payment-amount-breakdown-title',
  'ks.standard.payment-amount-breakdown-row',
]);

export const KsStandardRelatedMaterialScenePinpointFieldEnum = z.enum([
  'ks.standard.related-material-image',
  'ks.standard.related-material-remark',
]);

export const KsStandardPinpointFieldEnum = z.enum([
  ...KsStandardBasicInformationScenePinpointFieldEnum.options,
  ...KsStandardFundAdditionalAttributesScenePinpointFieldEnum.options,
  ...KsStandardActivityFlowScenePinpointFieldEnum.options,
  ...KsStandardActivitiesScenePinpointFieldEnum.options,
  ...KsStandardIncomeAndExpenditureScenePinpointFieldEnum.options,
  ...KsStandardFundBusinessScenePinpointFieldEnum.options,
  ...KsStandardOwnershipRatioScenePinpointFieldEnum.options,
  ...KsStandardInspectionEvaluationScenePinpointFieldEnum.options,
  ...KsStandardPaymentScenePinpointFieldEnum.options,
  ...KsStandardRelatedMaterialScenePinpointFieldEnum.options,
]);

export const KsStandardPinpointMultipleFieldEnum = z.enum([
  KsStandardBasicInformationScenePinpointFieldEnum.Enum[
    'ks.standard.legal-bases'
  ],
  KsStandardBasicInformationScenePinpointFieldEnum.Enum['ks.standard.plan'],
  KsStandardActivitiesScenePinpointFieldEnum.Enum['ks.standard.activity'],
  KsStandardPaymentScenePinpointFieldEnum.Enum['ks.standard.payment-group-row'],
  ...KsStandardActivityFlowScenePinpointFieldEnum.options,
  ...KsStandardFundBusinessScenePinpointFieldEnum.options,
  KsStandardPaymentScenePinpointFieldEnum.Enum[
    'ks.standard.payment-amount-breakdown-row'
  ],
]);

type KsStandardPinpointMultipleFieldEnumFieldEnumType =
  typeof KsStandardPinpointMultipleFieldEnum.Enum;

type projectPinpointMappingType = {
  path: z.infer<typeof projectPathObj>;
  field: Exclude<
    | z.infer<typeof KsStandardBasicInformationScenePinpointFieldEnum>
    | z.infer<typeof KsStandardFundAdditionalAttributesScenePinpointFieldEnum>
    | z.infer<typeof KsStandardActivitiesScenePinpointFieldEnum>
    | z.infer<typeof KsStandardIncomeAndExpenditureScenePinpointFieldEnum>
    | z.infer<typeof KsStandardOwnershipRatioScenePinpointFieldEnum>
    | z.infer<typeof KsStandardInspectionEvaluationScenePinpointFieldEnum>
    | z.infer<typeof KsStandardPaymentScenePinpointFieldEnum>
    | z.infer<typeof KsStandardRelatedMaterialScenePinpointFieldEnum>,
    z.infer<typeof KsStandardPinpointMultipleFieldEnum>
  >;
};

type FundBusinessPinpointMappingType = {
  path: z.infer<typeof fundBusinessIdProjectPathObj>;
  field: z.infer<typeof KsStandardFundBusinessScenePinpointFieldEnum>;
};

export type KsStandardActivityFlowPinpointMappingType = {
  path: z.infer<typeof activitiesActivityFlowProjectPathObj>;
  field: z.infer<typeof KsStandardActivityFlowScenePinpointFieldEnum>;
};

export type KsStandardActivity = {
  path: z.infer<typeof rootActivityPathObj>;
  field: KsStandardPinpointMultipleFieldEnumFieldEnumType['ks.standard.activity'];
};

export type KsStandardMultiplePinpointMappingType =
  | {
      path: z.infer<typeof legalBasesProjectPathObj>;
      field: KsStandardPinpointMultipleFieldEnumFieldEnumType['ks.standard.legal-bases'];
    }
  | {
      path: z.infer<typeof planProjectPathObj>;
      field: KsStandardPinpointMultipleFieldEnumFieldEnumType['ks.standard.plan'];
    }
  | {
      path: z.infer<typeof paymentGroupProjectPathObj>;
      field: KsStandardPinpointMultipleFieldEnumFieldEnumType['ks.standard.payment-group-row'];
    }
  | {
      path: z.infer<typeof paymentAmountBreakdownPathObj>;
      field: KsStandardPinpointMultipleFieldEnumFieldEnumType['ks.standard.payment-amount-breakdown-row'];
    }
  | KsStandardActivity
  | FundBusinessPinpointMappingType
  | KsStandardActivityFlowPinpointMappingType;

export type KsStandardPinpointMappingType =
  | projectPinpointMappingType
  | KsStandardMultiplePinpointMappingType;
